import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { Field } from "src/ui/shared/form/TextField";

import {
    Box,
    Chip,
    CircularProgress,
    ClickAwayListener,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListSubheader,
    Paper,
    Popper,
    useTheme,
} from "@mui/material";
import { Tooltip } from "@shared/Tooltip";

import { useStyles } from "./QueryBuilderField.styles";
import QueryBuilder, { ActiveFilter, Draft, FilterableAttribute, QueryBuilderOption } from "./QueryBuilderField.utils";

const initialDraft = { step: "key", key: null, operator: null, operatorLabel: null } as const;

type QueryBuilderFieldProps = {
    value?: string;
    onChange?: (value: string) => void;
    onClear?: () => void;
    onSubmit?: (value: string) => void;
    debounceWait?: number;
    loading?: boolean;
    name?: string;
    autoComplete?: string;
    placeholder?: string;
    options?: FilterableAttribute[];
};

export function QueryBuilderField({
    value = "",
    onChange,
    onClear,
    onSubmit,
    debounceWait = 700,
    loading = false,
    name,
    autoComplete,
    placeholder,
    options = [],
}: QueryBuilderFieldProps) {
    const isChipsMode = options.length > 0;
    const filters = isChipsMode ? QueryBuilder.parseStringToFilters(value) : [];
    const [draft, setDraft] = React.useState<Draft>(initialDraft);
    const [inputValue, setInputValue] = React.useState(filters.length === 0 ? value : "");
    const [open, setOpen] = React.useState(false);

    const { t } = useTranslation(["pages", "common"]);
    const { classes, cx } = useStyles();
    const theme = useTheme();

    const anchorRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const onChangeDebounced = React.useMemo(
        () => (onChange ? _.debounce(onChange, debounceWait) : undefined),
        [onChange, debounceWait],
    );

    const handleFocus = () => {
        if (draft.step !== "value") {
            setOpen(true);
        }
    };

    const handleSelectOption = (option: QueryBuilderOption) => {
        if (draft.step === "key") {
            onChangeDebounced?.cancel();
            setDraft({ step: "operator", key: option.value, operator: null, operatorLabel: null });
            setInputValue("");
            setOpen(true);
            inputRef.current?.focus();
            return;
        }
        if (draft.step === "operator") {
            setDraft((prev) => ({ ...prev, step: "value", operator: option.value, operatorLabel: option.label }));
            setInputValue("");
            setOpen(false);
            inputRef.current?.focus();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        if (!isChipsMode) {
            onChangeDebounced?.(newValue);
            return;
        }

        if (draft.step !== "value") {
            setOpen(true);
        }
        if (draft.step === "key" && filters.length === 0) {
            // Until a key is picked, treat typing as free-text search; handleSelectOption cancels this once chip mode is confirmed.
            if (!newValue.trim()) {
                onChangeDebounced?.cancel();
                onChange?.("");
            } else {
                onChangeDebounced?.(newValue.trim());
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isChipsMode) {
            if (e.key === "Enter" && onSubmit) {
                e.preventDefault();
                onChangeDebounced?.flush();
                onSubmit(inputValue);
            }
            return;
        }

        if (e.key === "Enter" && draft.step === "key" && inputValue.trim()) {
            onChangeDebounced?.cancel();
            onChange?.(inputValue.trim());
            setOpen(false);
            return;
        }
        if (e.key === "Enter" && draft.step === "value" && inputValue.trim() && draft.key && draft.operator) {
            const newFilter: ActiveFilter = {
                key: draft.key,
                operator: draft.operator,
                value: inputValue.trim(),
            };
            const newFilters = [...filters, newFilter];
            onChange?.(QueryBuilder.parseFiltersToString(newFilters));
            setDraft(initialDraft);
            setInputValue("");
        }
        if (e.key === "Escape") {
            onChangeDebounced?.cancel();
            setDraft(initialDraft);
            setInputValue("");
            setOpen(false);
            onChange?.("");
        }
    };

    const removeFilter = (index: number) => {
        const newFilters = filters.filter((_f, i) => i !== index);
        onChange?.(QueryBuilder.parseFiltersToString(newFilters));
    };

    const handleClear = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();
        onChangeDebounced?.cancel();
        setDraft(initialDraft);
        setInputValue("");
        setOpen(false);
        if (isChipsMode) {
            onChange?.("");
        }
        onClear?.();
    };

    const resetDraft = () => {
        setDraft(initialDraft);
        setInputValue("");
        setOpen(false);
    };

    const getPlaceholder = () => {
        if (!isChipsMode) {
            return placeholder;
        }
        if (filters.length > 0 && draft.step === "key") {
            return "";
        }

        return {
            key: t("pages:queryBuilderField.placeholderKey"),
            operator: t("pages:queryBuilderField.placeholderOperator"),
            value: t("pages:queryBuilderField.placeholderValue"),
        }[draft.step];
    };

    const getCurrentOptions = () => {
        if (!isChipsMode || draft.step === "value") {
            return [];
        }
        const operators = QueryBuilder.getOperators();
        const sortedOptions = _.sortBy(options, "label");
        const pool = draft.step === "key" ? sortedOptions.map((o) => ({ label: o.label, value: o.key })) : operators;
        const normalizedInput = inputValue.toLowerCase();

        return inputValue.trim() === "" ? pool : pool.filter((option) => option.label.toLowerCase().includes(normalizedInput));
    };

    const currentOptions = getCurrentOptions();
    const hasClearableContent = filters.length > 0 || inputValue.length > 0;

    React.useEffect(() => {
        if (isChipsMode) {
            setInputValue(QueryBuilder.parseStringToFilters(value).length === 0 ? value : "");
        } else {
            setInputValue(value);
        }
    }, [value, isChipsMode]);

    React.useEffect(() => {
        return () => {
            onChangeDebounced?.cancel();
        };
    }, [onChangeDebounced]);

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box ref={anchorRef} className={classes.root}>
                <Field
                    inputRef={inputRef}
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={getPlaceholder()}
                    slotProps={{
                        htmlInput: { style: { paddingLeft: theme.spacing(1) }, name, autoComplete },
                        input: {
                            startAdornment: (
                                <InputAdornment position="start" className={classes.startAdornment}>
                                    <i className={cx("fas fa-search", classes.searchIcon)} />
                                    {isChipsMode ? (
                                        <Box className={classes.chipsArea}>
                                            {filters.map((filter, i) => {
                                                const attributeLabel = QueryBuilder.getAttributeLabel(options, filter.key);
                                                const label = `${attributeLabel} ${QueryBuilder.getOperatorLabel(filter.operator)} ${filter.value}`;
                                                return (
                                                    <Chip
                                                        key={label}
                                                        className={classes.chip}
                                                        size="small"
                                                        label={label}
                                                        onDelete={() => removeFilter(i)}
                                                    />
                                                );
                                            })}
                                            {draft.key ? (
                                                <Chip
                                                    className={classes.draftChip}
                                                    size="small"
                                                    label={draft.key}
                                                    onDelete={resetDraft}
                                                />
                                            ) : null}
                                            {draft.operator ? (
                                                <Chip
                                                    className={classes.draftChip}
                                                    size="small"
                                                    label={draft.operatorLabel ?? draft.operator}
                                                    onDelete={() => {
                                                        setDraft((prev) => ({
                                                            ...prev,
                                                            step: "operator",
                                                            operator: null,
                                                            operatorLabel: null,
                                                        }));
                                                        setInputValue("");
                                                        setOpen(true);
                                                        inputRef.current?.focus();
                                                    }}
                                                />
                                            ) : null}
                                        </Box>
                                    ) : null}
                                </InputAdornment>
                            ),
                            endAdornment: loading ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={16} />
                                </InputAdornment>
                            ) : hasClearableContent ? (
                                <InputAdornment position="end">
                                    <Tooltip title={t("common:button.clear")}>
                                        <IconButton size="small" aria-label={t("common:button.clear")} onClick={handleClear}>
                                            <i className="fa-solid fa-xmark" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                />

                <Popper
                    open={open && currentOptions.length > 0}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    style={{ zIndex: 1300, width: anchorRef.current?.clientWidth }}
                >
                    <Paper elevation={4}>
                        <List
                            dense
                            disablePadding
                            subheader={
                                <ListSubheader disableSticky>
                                    {draft.step === "key"
                                        ? t("pages:queryBuilderField.applyFilter")
                                        : t("pages:queryBuilderField.placeholderOperator")}
                                </ListSubheader>
                            }
                        >
                            {currentOptions.map((option) => (
                                <ListItemButton
                                    key={`${draft.step}-${option.value}`}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleSelectOption(option)}
                                >
                                    {option.label}
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
}
